// =============================================================================
// features/admin/feature-flags/audienceImpact.ts — Lógica pura do resumo de
// impacto e da semântica AND/OR da audience (F32-S04).
//
// Espelha a regra do avaliador único do backend
// (apps/api/src/lib/featureFlags.ts#isFeatureEnabledForContext):
//   - status 'enabled'  → libera geral, audience não é avaliada.
//   - status 'disabled' → nega geral, audience não é avaliada.
//   - status 'internal_only' → cada eixo PRESENTE E PREENCHIDO restringe
//     (fail-closed); eixo ausente/vazio NÃO restringe (fail-open só nesse
//     caso — comportamento legado preservado deliberadamente). Eixos
//     diferentes combinam com E; valores do mesmo eixo combinam com OU.
//
// IMPORTANTE — limite conhecido e deliberado (doc26 §21):
//   Não existe endpoint de contagem/preview no backend (fora de escopo do
//   F32-S03) e a UI NUNCA lista o diretório inteiro de usuários para somar
//   quem bate com papel/cidade/organização — isso violaria a regra de não
//   despejar o diretório (ver F32-S04, nota "Não listar..."). Por isso só o
//   eixo `user_ids` tem contagem EXATA (é uma lista fechada de UUIDs
//   escolhidos a dedo). Os demais eixos aparecem de forma DESCRITIVA no
//   resumo, com uma recomendação explícita de testar com um login real.
// =============================================================================

import type {
  FeatureFlagAudience,
  FeatureFlagStatus,
} from '../../../hooks/admin/useFeatureFlags.types';

export type AudienceAxisKey = 'roles' | 'city_ids' | 'organization_ids' | 'user_ids';

export interface AudienceDraft {
  roles: string[];
  city_ids: string[];
  organization_ids: string[];
  user_ids: string[];
}

export interface AxisLabelResolvers {
  /** Resolve um role key ('admin') para o label exibível ('Admin'). */
  roleLabel: (key: string) => string;
  /** Resolve um UUID de cidade para o nome do município. */
  cityLabel: (id: string) => string;
}

export interface AudienceAxisSummary {
  key: AudienceAxisKey;
  label: string;
  values: string[];
  displayValues: string[];
  isEmpty: boolean;
}

export type ImpactSeverity = 'neutral' | 'info' | 'warning' | 'danger';

export interface AudienceImpactSummary {
  status: FeatureFlagStatus;
  /** true quando a audience é de fato avaliada (só em internal_only). */
  audienceApplies: boolean;
  /** true quando, hoje, TODO usuário autenticado recebe a flag. */
  opensToEveryone: boolean;
  axes: AudienceAxisSummary[];
  filledAxesCount: number;
  /** Contagem exata — só o eixo user_ids garante isso (lista fechada de UUIDs). */
  exactUserCount: number;
  headline: string;
  sentence: string;
  hint: string;
  severity: ImpactSeverity;
}

const AXIS_LABELS: Record<AudienceAxisKey, string> = {
  roles: 'Papéis',
  city_ids: 'Cidades',
  organization_ids: 'Organizações',
  user_ids: 'Usuários específicos',
};

/** Normaliza um valor bruto de eixo removendo duplicatas e vazios. */
function cleanAxis(values: string[]): string[] {
  return Array.from(new Set(values.map((v) => v.trim()).filter((v) => v.length > 0)));
}

function buildAxisSummary(
  key: AudienceAxisKey,
  rawValues: string[],
  resolveLabel: (v: string) => string,
): AudienceAxisSummary {
  const values = cleanAxis(rawValues);
  return {
    key,
    label: AXIS_LABELS[key],
    values,
    displayValues: values.map(resolveLabel),
    isEmpty: values.length === 0,
  };
}

/**
 * Converte o form draft em `FeatureFlagAudience` pronto pro PATCH.
 * O backend SUBSTITUI o objeto `audience` inteiro (não mescla por eixo) —
 * por isso os 4 eixos são sempre enviados juntos, mesmo vazios.
 */
export function audienceDraftToPayload(draft: AudienceDraft): FeatureFlagAudience {
  return {
    roles: cleanAxis(draft.roles),
    city_ids: cleanAxis(draft.city_ids),
    organization_ids: cleanAxis(draft.organization_ids),
    user_ids: cleanAxis(draft.user_ids),
  };
}

/** Converte a audience persistida (pode vir undefined por eixo) em draft de form. */
export function audienceToDraft(audience: FeatureFlagAudience | undefined): AudienceDraft {
  return {
    roles: audience?.roles ?? [],
    city_ids: audience?.city_ids ?? [],
    organization_ids: audience?.organization_ids ?? [],
    user_ids: audience?.user_ids ?? [],
  };
}

/**
 * Monta o resumo de impacto + a frase de semântica E/OU exibida antes de
 * salvar (DoD do F32-S04). Função pura — testada isoladamente.
 */
export function buildAudienceImpact(
  status: FeatureFlagStatus,
  draft: AudienceDraft,
  resolvers: AxisLabelResolvers,
): AudienceImpactSummary {
  const axes: AudienceAxisSummary[] = [
    buildAxisSummary('roles', draft.roles, resolvers.roleLabel),
    buildAxisSummary('city_ids', draft.city_ids, resolvers.cityLabel),
    buildAxisSummary('organization_ids', draft.organization_ids, (v) => v),
    buildAxisSummary('user_ids', draft.user_ids, (v) => v),
  ];
  const filledAxes = axes.filter((a) => !a.isEmpty);
  const exactUserCount = axes.find((a) => a.key === 'user_ids')?.values.length ?? 0;

  if (status === 'enabled') {
    return {
      status,
      audienceApplies: false,
      opensToEveryone: true,
      axes,
      filledAxesCount: filledAxes.length,
      exactUserCount,
      headline: 'Habilitada para todo mundo',
      sentence:
        'O status está HABILITADO — a flag libera para qualquer usuário autenticado. ' +
        'Os filtros de audiência abaixo NÃO são avaliados neste status.',
      hint: 'Para restringir por audiência, mude o status para "Interno" antes de salvar.',
      severity: 'info',
    };
  }

  if (status === 'disabled') {
    return {
      status,
      audienceApplies: false,
      opensToEveryone: false,
      axes,
      filledAxesCount: filledAxes.length,
      exactUserCount,
      headline: 'Desabilitada para todo mundo',
      sentence:
        'O status está DESABILITADO — ninguém recebe esta flag, independente da audiência configurada.',
      hint: 'Os eixos abaixo continuam salvos, mas só passam a valer se o status mudar para "Interno".',
      severity: 'neutral',
    };
  }

  // status === 'internal_only'
  if (filledAxes.length === 0) {
    return {
      status,
      audienceApplies: true,
      opensToEveryone: true,
      axes,
      filledAxesCount: 0,
      exactUserCount,
      headline: 'Atenção: libera para TODOS',
      sentence:
        'Nenhum eixo preenchido restringe quem recebe. Com status "Interno" e todos os eixos ' +
        'vazios, TODO usuário autenticado recebe a flag hoje (comportamento herdado, preservado ' +
        'de propósito para não regredir flags já em produção).',
      hint:
        'Se a intenção é liberar só para o piloto, preencha ao menos "Usuários específicos" ' +
        'e/ou "Organizações" antes de salvar.',
      severity: 'warning',
    };
  }

  const clauses = filledAxes.map(
    (a) => `${a.label.toLowerCase()}: ${a.displayValues.join(' OU ')}`,
  );

  const onlyUserAxis = filledAxes.length === 1 && filledAxes[0]?.key === 'user_ids';
  const hint = onlyUserAxis
    ? `Contagem exata: ${exactUserCount} usuário(s) específico(s) listado(s) abaixo recebe(m) a flag.`
    : 'O número exato de pessoas atingidas por papel/cidade/organização depende de quantos ' +
      'usuários reais têm essa combinação hoje — a tela não despeja o diretório inteiro para ' +
      'somar isso. Teste com o login de um usuário representativo (e de um que NÃO deveria ' +
      'receber) antes de confiar no piloto.';

  return {
    status,
    audienceApplies: true,
    opensToEveryone: false,
    axes,
    filledAxesCount: filledAxes.length,
    exactUserCount,
    headline:
      filledAxes.length === 1
        ? `Restrita por ${filledAxes[0]?.label.toLowerCase()}`
        : `Restrita por ${filledAxes.length} condições combinadas`,
    sentence:
      'Libera só para quem atender TODAS as condições abaixo ao mesmo tempo (E). Dentro de cada ' +
      `condição, qualquer valor listado libera (OU): ${clauses.join('; ')}.`,
    hint,
    severity: onlyUserAxis ? 'info' : 'warning',
  };
}

// ---------------------------------------------------------------------------
// F38-S16 — legibilidade "audiência vale" vs "audiência ignorada" e texto de
// confirmação ao SAIR de internal_only para enabled (promoção que amplia a
// audiência para todo mundo, silenciosamente, se a tela não avisar).
// ---------------------------------------------------------------------------

export type AudienceApplicabilityTone = 'applies' | 'ignored' | 'stored';

export interface AudienceApplicabilityBadge {
  label: string;
  tone: AudienceApplicabilityTone;
}

/**
 * Descreve, para o status ATUAL da flag, se a audiência configurada é
 * avaliada (`internal_only`), completamente ignorada (`enabled`, libera
 * geral) ou apenas guardada sem efeito (`disabled`, nega geral). Espelha a
 * mesma regra de `buildAudienceImpact`, mas sem exigir resolvers — usada em
 * lugares compactos da tabela onde só o rótulo importa.
 */
export function describeAudienceApplicability(
  status: FeatureFlagStatus,
): AudienceApplicabilityBadge {
  if (status === 'internal_only') {
    return { label: 'Audiência vale', tone: 'applies' };
  }
  if (status === 'enabled') {
    return { label: 'Audiência ignorada', tone: 'ignored' };
  }
  return { label: 'Audiência guardada', tone: 'stored' };
}

/**
 * Frase de confirmação ao promover `internal_only` → `enabled` — a única
 * transição que amplia quem vê a flag (doc 09 + F38-S16). Recebe o impacto
 * já calculado para o status `internal_only` ATUAL (antes da promoção) e
 * descreve, com número quando possível, quem passa a ver a flag.
 */
export function describePromotionImpact(impact: AudienceImpactSummary): string {
  if (impact.filledAxesCount === 0) {
    return (
      'Hoje, com a audiência vazia, esta flag "Interno" já libera para todo mundo ' +
      '(comportamento herdado, preservado de propósito). Habilitar não muda quem vê agora, mas ' +
      'passa a ignorar de vez qualquer audiência que você configurar aqui depois.'
    );
  }

  const onlyUserAxis =
    impact.filledAxesCount === 1 && impact.axes.some((a) => a.key === 'user_ids' && !a.isEmpty);

  if (onlyUserAxis) {
    return (
      `Hoje só ${impact.exactUserCount} usuário(s) específico(s) enxerga(m) esta flag. ` +
      'Ao habilitar, TODOS os usuários autenticados passam a enxergar — a lista configurada deixa ' +
      'de filtrar quem vê.'
    );
  }

  return (
    'Hoje a audiência configurada restringe quem vê esta flag (por papel, cidade e/ou ' +
    'organização). Ao habilitar, essa restrição deixa de valer e TODOS os usuários autenticados ' +
    'passam a enxergar — sem forma de calcular quantos exatamente sem consultar o diretório.'
  );
}
